import React, { Component } from 'react';
import '../../../../../styles/components/our-partner.scss';
import { getClientsNew } from '../../../../../models/organization';
import i18n from '../../../../../i18n';

class PartnersSectionNew extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
      partners: [],
      loading: false,
      activeIndex: 0,
      isTransitioning: false,
    };
    this.sectionRef = React.createRef();
    this.autoplayTimer = null;
    this.transitionTimer = null;
  }

  componentDidMount() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState({ isVisible: true });
            if (this.state.partners.length === 0) {
              this.loadPartners();
            }
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (this.sectionRef.current) {
      this.observer.observe(this.sectionRef.current);
    }
  }

  componentWillUnmount() {
    if (this.observer && this.sectionRef.current) {
      this.observer.unobserve(this.sectionRef.current);
    }
    this.stopAutoplay();
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
  }

  async loadPartners() {
    try {
      this.setState({ loading: true });

      const response = await getClientsNew();

      const partners = (response?.data || []).map((item) => ({
        id: item.id,
        name: item.organization_name,
        name_ar: item.organization_name_ar,
        logo: item.organization_image_url,
      }));

      this.setState({ partners, loading: false }, () => {
        if (partners.length > 1) {
          this.startAutoplay();
        }
      });
    } catch (error) {
      console.error('Failed to load partners', error);
      this.setState({ loading: false });
    }
  }

  startAutoplay = () => {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.goNext();
    }, 1500);
  };

  stopAutoplay = () => {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  goNext = () => {
    const { partners, isTransitioning } = this.state;
    if (isTransitioning || partners.length <= 1) return;

    this.setState((prev) => ({
      activeIndex: (prev.activeIndex + 1) % partners.length,
      isTransitioning: true,
    }));

    this.transitionTimer = setTimeout(() => {
      this.setState({ isTransitioning: false });
    }, 600);
  };

  goPrev = () => {
    const { partners, isTransitioning } = this.state;
    if (isTransitioning || partners.length <= 1) return;

    this.setState((prev) => ({
      activeIndex: (prev.activeIndex - 1 + partners.length) % partners.length,
      isTransitioning: true,
    }));

    this.transitionTimer = setTimeout(() => {
      this.setState({ isTransitioning: false });
    }, 600);
  };

  handleDotClick = (index) => {
    const { isTransitioning } = this.state;
    if (isTransitioning) return;

    this.stopAutoplay();
    this.setState({ activeIndex: index, isTransitioning: true });

    this.transitionTimer = setTimeout(() => {
      this.setState({ isTransitioning: false });
      this.startAutoplay();
    }, 600);
  };

  // Get the visible cards: 3 on each side + center = 7 visible
  getVisibleCards = () => {
    const { partners, activeIndex } = this.state;
    const len = partners.length;
    if (len === 0) return [];

    const positions = [-3, -2, -1, 0, 1, 2, 3];
    return positions.map((offset) => {
      const idx = ((activeIndex + offset) % len + len) % len;
      return {
        partner: partners[idx],
        offset,
        index: idx,
      };
    });
  };

  render() {
    const { isVisible, partners, loading, activeIndex } = this.state;
    const isArabic = i18n.language.startsWith('ar');
    const visibleCards = this.getVisibleCards();

    return (
      <section
        ref={this.sectionRef}
        className={`mzp-section ${isVisible ? 'mzp-section--visible' : ''}`}
      >
        <div className="mzp-section__inner">
          {/* Header */}
          <div className="mzp-section__header">
            <span className="mzp-section__label">
              {i18n.t('trusted_by') || 'Trusted By'}
            </span>
            <h2 className="mzp-section__title">
              {i18n.t('our_partners') || 'Our Partner'}
            </h2>
            <p className="mzp-section__desc">
              {i18n.t('partner_description') ||
                'We collaborate with industry leaders to bring you the best auction experience'}
            </p>
          </div>

          {/* Loading Skeleton */}
          {loading && (
            <div className="mzp-carousel">
              <div className="mzp-carousel__track">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={`skel-${i}`}
                    className={`mzp-carousel__card mzp-carousel__card--offset-${i - 2}`}
                  >
                    <div className="mzp-carousel__card-inner mzp-carousel__skeleton" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Carousel */}
          {!loading && partners.length > 0 && (
            <div
              className="mzp-carousel"
              onMouseEnter={this.stopAutoplay}
              onMouseLeave={this.startAutoplay}
            >
              <div className="mzp-carousel__track">
                {visibleCards.map(({ partner, offset, index }) => (
                  <div
                    key={`${partner.id}-${offset}`}
                    className={`mzp-carousel__card mzp-carousel__card--offset-${offset}`}
                    onClick={() => {
                      if (offset !== 0) this.handleDotClick(index);
                    }}
                  >
                    <div className="mzp-carousel__card-inner">
                      <img
                        src={partner.logo}
                        alt={isArabic ? partner.name_ar : partner.name}
                        className="mzp-carousel__logo"
                        draggable={false}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Edge fade masks */}
              <div className="mzp-carousel__fade mzp-carousel__fade--left" />
              <div className="mzp-carousel__fade mzp-carousel__fade--right" />
            </div>
          )}

          {/* Dots */}
          {!loading && partners.length > 1 && (
            <div className="mzp-dots">
              {partners.map((_, i) => (
                <button
                  key={i}
                  className={`mzp-dots__dot ${i === activeIndex ? 'mzp-dots__dot--active' : ''}`}
                  onClick={() => this.handleDotClick(i)}
                  aria-label={`Go to partner ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && partners.length === 0 && (
            <div className="mzp-section__empty">
              <p>{i18n.t('no_partners') || 'No partners to display'}</p>
            </div>
          )}
        </div>
      </section>
    );
  }
}

export default PartnersSectionNew;